/**
 * Excel Export Adapter for Manufacturers (issue #653, Task 9, Sub-task 9.3)
 *
 * Column set derives from the same field registry the grid/search/modal
 * adapters already read (`getFieldsForSurface('export')`) — the same "one
 * registry drives every surface" pattern Products adopted in issue #585,
 * applied to manufacturers for the first time (there was no manufacturer
 * export path at all before this sub-task).
 *
 * Deliberately a small, self-contained module rather than a generalization
 * of `client/src/components/buildExcelWorkbook.ts` — that file is built
 * specifically around Products' two-sheet (raw + deduplicated) fan-out
 * shape (`FlatProduct`, `ARRAY_FIELD_KEYS`), which doesn't fit
 * Manufacturers' single nested-entity shape (`facilities`/`products` are
 * arrays ON one row, not a row that fans out per relation). Reusing it
 * would have meant either reshaping Manufacturer rows to fit Products'
 * assumptions or adding manufacturer-specific branches to a file that
 * currently has none — both riskier than one small dedicated module.
 */

import ExcelJS from 'exceljs';
import { Manufacturer } from '../../../services/api/domains/manufacturers/types';
import { BaseFieldDefinition } from '../../../shared/types/fieldRegistry';
import { getFieldsForSurface } from '../fields';
import { UNCONNECTED_LINKAGE_FIELD_KEYS } from '../linkageSources';

function formatCellValue(
  field: BaseFieldDefinition<Manufacturer>,
  manufacturer: Manufacturer
): string | number {
  const value = manufacturer[field.key as keyof Manufacturer];

  if (field.dataType === 'boolean') {
    if (UNCONNECTED_LINKAGE_FIELD_KEYS.has(field.key as string)) {
      return 'N/A (not yet connected)';
    }
    if (value === null || value === undefined) return '';
    return value ? 'Yes' : 'No';
  }

  if (field.isRelation && field.relationConfig && Array.isArray(value)) {
    // Multi-value fields render consistently with the grid (Sub-task 9.3
    // AC) — same displayField the grid's own renderCell uses
    // (dataGridAdapter.tsx), joined in full rather than the grid's
    // 3-item-plus-overflow truncation (export isn't space-constrained).
    const { displayField } = field.relationConfig;
    // ProductSummary/FacilitySummary have no index signature, and
    // displayField is a runtime string from relationConfig (not a static
    // keyof) -- unlike dataGridAdapter.tsx's per-field renderers (each
    // narrowed to exactly ONE relation type), `value` here is the full
    // union across every relation field this adapter handles, which is too
    // wide for a `keyof typeof item` cast to resolve to anything but
    // `never`. The widen-via-unknown step is the correct escape for that
    // wider union, not a corner cut.
    const items = value as unknown as Array<Record<string, unknown>>;
    return items
      .map(item => item[displayField])
      .filter((name): name is string => typeof name === 'string' && !!name)
      .join(', ');
  }

  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === 'number' || typeof value === 'string') return value;
  return String(value);
}

/** Apply bold header styling to the first row of a worksheet. */
function styleHeaderRow(row: ExcelJS.Row): void {
  row.font = { bold: true };
  row.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFD6E4F0' },
  } as ExcelJS.FillPattern;
  row.alignment = { vertical: 'middle' };
}

/**
 * Builds the underlying ExcelJS workbook from the given manufacturers, with
 * columns derived from `getFieldsForSurface('export')`. Separated from the
 * Blob-returning wrapper below so tests can load it back with ExcelJS
 * directly (jsdom's `Blob` has no working `arrayBuffer()`, so round-
 * tripping through a real `Blob` isn't viable in Jest).
 */
export function buildManufacturersWorkbook(
  manufacturers: Manufacturer[]
): ExcelJS.Workbook {
  const fields = getFieldsForSurface('export');

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Manufacturers');

  sheet.columns = fields.map(field => ({
    header: field.label,
    key: field.key as string,
    width: Math.max(12, Math.round((field.renderConfig?.width ?? 150) / 7)),
  }));
  styleHeaderRow(sheet.getRow(1));

  manufacturers.forEach(manufacturer => {
    const row: Record<string, string | number> = {};
    fields.forEach(field => {
      row[field.key as string] = formatCellValue(field, manufacturer);
    });
    sheet.addRow(row);
  });

  return workbook;
}

/**
 * Builds an Excel workbook (one worksheet) from the given manufacturers,
 * ready to hand to `downloadBlob()`.
 */
export async function buildManufacturersExcelWorkbook(
  manufacturers: Manufacturer[]
): Promise<Blob> {
  const workbook = buildManufacturersWorkbook(manufacturers);
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

/** Triggers a browser download of the given blob under the given filename. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
